/**
 * The search state behind a `SubjectPicker`: what was typed, debounced, run
 * through the caller's search, and the options and loading flag that come back.
 *
 * `useRecordSearch` with two things fixed: the answer is `{ subjects }`, as
 * every directory endpoint returns it, and **an empty term searches nothing** —
 * a directory is too large to offer before anything is typed.
 *
 * Takes the search as an argument, so it knows no endpoint: `/api/subjects`,
 * users only, messaging's recipients and the CRM's owners are all the same to it.
 */
import type { SubjectDto } from '@forsys/shared';
import { useRecordSearch, type RecordSearch, type RecordSearchOptions } from './useRecordSearch';

export type SubjectSearchOptions = Pick<RecordSearchOptions, 'enabled'>;

export interface SubjectSearch extends Omit<RecordSearch<SubjectDto>, 'options'> {
  /** What the search returned for the current term; empty while there is none. */
  options: SubjectDto[];
}

export function useSubjectSearch(
  search: (query: string) => Promise<{ subjects: SubjectDto[] }>,
  { enabled = true }: SubjectSearchOptions = {},
): SubjectSearch {
  const found = useRecordSearch(async (query) => (await search(query)).subjects, { enabled, searchEmpty: false });
  return { ...found, options: [...found.options] };
}
